import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Link } from 'react-router-dom'
import { Lock, Play, Info } from 'lucide-react'
import { User } from 'firebase/auth'
import { useEffect, useState } from 'react'
import { getUserSubscription, isPremiumSubscription } from '@/services/subscriptionService'

interface HomePageProps {
  user: User | null
}

export default function HomePage({ user }: HomePageProps) {
  const [isPremium, setIsPremium] = useState(false)

  useEffect(() => {
    const checkSubscription = async () => {
      if (user) {
        const subscription = await getUserSubscription(user.uid)
        setIsPremium(isPremiumSubscription(subscription))
      } else {
        setIsPremium(false)
      }
    }
    checkSubscription()
  }, [user])

  return (
    <div className="container mx-auto px-4 py-8 flex flex-col items-center justify-center min-h-[80vh] space-y-12">
      {/* Hero Section */}
      <div className="text-center space-y-6 max-w-3xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-1000">
        <div className="relative mx-auto w-64 h-64 md:w-80 md:h-80 mb-8">
          <img
            src="/theas-games-logo-transparent.png"
            alt="Thea's Games Logo"
            className="w-full h-full object-contain drop-shadow-xl hover:scale-105 transition-transform duration-300"
          />
        </div>

        <h1 className="text-5xl md:text-6xl font-bold text-dark-navy tracking-tight drop-shadow-sm">
          Thea's Games
        </h1>

        <p className="text-xl md:text-2xl text-dark-navy/80 max-w-2xl mx-auto font-medium">
          A magical underwater world of fun, learning, and ocean conservation.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
          <Link to="/simple-match/play">
            <Button size="lg" className="text-lg px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all bg-medium-teal hover:bg-light-teal text-white border-2 border-white/20">
              Play Now
            </Button>
          </Link>
          {!user && (
            <Link to="/auth">
              <Button variant="secondary" size="lg" className="text-lg px-8 py-6 rounded-full shadow-md hover:shadow-lg transition-all bg-medium-purple hover:bg-deep-purple text-white">
                Create Account
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Free Games Section */}
      <div className="w-full max-w-5xl px-4">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-1 flex-1 bg-gradient-to-r from-transparent to-medium-teal/30 rounded-full" />
          <h2 className="text-2xl md:text-3xl font-bold text-dark-navy flex items-center gap-2">
            <span className="text-3xl">🎮</span> Free Games
          </h2>
          <div className="h-1 flex-1 bg-gradient-to-l from-transparent to-medium-teal/30 rounded-full" />
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {/* Simple Match Card */}
          <Card className="bg-white/90 backdrop-blur-sm border-2 border-pale-aqua hover:border-medium-teal transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1 group">
            <CardHeader className="pb-2">
              <div className="text-4xl mb-2">🐚</div>
              <CardTitle className="text-xl text-dark-navy group-hover:text-medium-teal transition-colors">Simple Match</CardTitle>
              <CardDescription className="text-medium-purple font-medium text-sm">Classic Memory Game</CardDescription>
            </CardHeader>
            <CardContent className="pb-4">
              <p className="text-dark-navy/70 text-sm">
                Flip shells to find matching pairs of tropical fish, seahorses, and more!
              </p>
            </CardContent>
            <CardFooter className="flex gap-2">
              <Link to="/simple-match/play" className="flex-1">
                <Button className="w-full bg-medium-teal hover:bg-light-teal text-white">
                  <Play className="w-4 h-4 mr-2" />
                  Play
                </Button>
              </Link>
              <Link to="/simple-match">
                <Button variant="outline" className="border-medium-teal text-medium-teal hover:bg-medium-teal hover:text-white">
                  <Info className="w-4 h-4" />
                </Button>
              </Link>
            </CardFooter>
          </Card>

          {/* Ocean Dash Card */}
          <Card className="bg-gradient-to-br from-white/95 to-light-teal/20 backdrop-blur-sm border-2 border-bright-coral hover:border-sandy-coral transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1 group relative overflow-hidden">
            <div className="absolute top-2 right-2 bg-bright-coral text-white text-xs px-2 py-1 rounded-full font-bold animate-pulse">
              NEW!
            </div>
            <CardHeader className="pb-2">
              <div className="text-4xl mb-2">🧜‍♀️</div>
              <CardTitle className="text-xl text-dark-navy group-hover:text-bright-coral transition-colors">Ocean Dash</CardTitle>
              <CardDescription className="text-medium-purple font-medium text-sm">Endless Swimming Adventure</CardDescription>
            </CardHeader>
            <CardContent className="pb-4">
              <p className="text-dark-navy/70 text-sm">
                Help Thea swim through the ocean, collect pearls, and avoid jellyfish!
              </p>
            </CardContent>
            <CardFooter className="flex gap-2">
              <Link to="/ocean-dash/play" className="flex-1">
                <Button className="w-full bg-bright-coral hover:bg-sandy-coral text-white">
                  <Play className="w-4 h-4 mr-2" />
                  Play
                </Button>
              </Link>
              <Link to="/ocean-dash">
                <Button variant="outline" className="border-bright-coral text-bright-coral hover:bg-bright-coral hover:text-white">
                  <Info className="w-4 h-4" />
                </Button>
              </Link>
            </CardFooter>
          </Card>
        </div>
      </div>

      {/* Premium Games Section */}
      <div className="w-full max-w-5xl px-4">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-1 flex-1 bg-gradient-to-r from-transparent to-medium-purple/30 rounded-full" />
          <h2 className="text-2xl md:text-3xl font-bold text-dark-navy flex items-center gap-2">
            <span className="text-3xl">⭐</span> Premium
          </h2>
          <div className="h-1 flex-1 bg-gradient-to-l from-transparent to-medium-purple/30 rounded-full" />
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Pollution Patrol Card */}
          <Card className={`bg-white/90 backdrop-blur-sm border-2 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1 group relative overflow-hidden ${isPremium ? 'border-pale-aqua hover:border-medium-teal' : 'border-medium-purple/50'}`}>
            {!isPremium && (
              <div className="absolute top-2 right-2 bg-medium-purple text-white text-xs px-2 py-1 rounded-full font-bold flex items-center gap-1">
                <Lock className="w-3 h-3" />
                Premium
              </div>
            )}
            <CardHeader className="pb-2">
              <div className="text-4xl mb-2">♻️</div>
              <CardTitle className={`text-xl text-dark-navy transition-colors ${isPremium ? 'group-hover:text-medium-teal' : 'group-hover:text-medium-purple'}`}>Pollution Patrol</CardTitle>
              <CardDescription className="text-medium-purple font-medium text-sm">Ocean Cleanup Mission</CardDescription>
            </CardHeader>
            <CardContent className="pb-4">
              <p className="text-dark-navy/70 text-sm">
                Help Thea clean up the ocean by collecting trash while protecting marine life!
              </p>
            </CardContent>
            <CardFooter className="flex gap-2">
              {isPremium ? (
                <>
                  <Link to="/pollution-patrol/play" className="flex-1">
                    <Button className="w-full bg-medium-teal hover:bg-light-teal text-white">
                      <Play className="w-4 h-4 mr-2" />
                      Play
                    </Button>
                  </Link>
                  <Link to="/pollution-patrol">
                    <Button variant="outline" className="border-medium-teal text-medium-teal hover:bg-medium-teal hover:text-white">
                      <Info className="w-4 h-4" />
                    </Button>
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/pollution-patrol" className="flex-1">
                    <Button variant="outline" className="w-full border-medium-purple text-medium-purple hover:bg-medium-purple hover:text-white">
                      <Info className="w-4 h-4 mr-2" />
                      Learn More
                    </Button>
                  </Link>
                  <Link to="/subscription">
                    <Button className="bg-medium-purple hover:bg-deep-purple text-white">
                      <Lock className="w-4 h-4" />
                    </Button>
                  </Link>
                </>
              )}
            </CardFooter>
          </Card>

          {/* Pearl Pop Card */}
          <Card className={`bg-gradient-to-br from-white/95 to-medium-purple/10 backdrop-blur-sm border-2 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1 group relative overflow-hidden ${isPremium ? 'border-sandy-coral hover:border-bright-coral' : 'border-medium-purple/50'}`}>
            {!isPremium && (
              <div className="absolute top-2 right-2 bg-medium-purple text-white text-xs px-2 py-1 rounded-full font-bold flex items-center gap-1">
                <Lock className="w-3 h-3" />
                Premium
              </div>
            )}
            {isPremium && (
              <div className="absolute top-2 right-2 bg-bright-coral text-white text-xs px-2 py-1 rounded-full font-bold animate-pulse">
                NEW!
              </div>
            )}
            <CardHeader className="pb-2">
              <div className="text-4xl mb-2">🦪</div>
              <CardTitle className={`text-xl text-dark-navy transition-colors ${isPremium ? 'group-hover:text-bright-coral' : 'group-hover:text-medium-purple'}`}>Pearl Pop</CardTitle>
              <CardDescription className="text-medium-purple font-medium text-sm">Bubble Shooter Puzzle</CardDescription>
            </CardHeader>
            <CardContent className="pb-4">
              <p className="text-dark-navy/70 text-sm">
                Aim and shoot to match colourful pearls. Pop them before they reach the danger line!
              </p>
            </CardContent>
            <CardFooter className="flex gap-2">
              {isPremium ? (
                <>
                  <Link to="/pearl-pop/play" className="flex-1">
                    <Button className="w-full bg-bright-coral hover:bg-sandy-coral text-white">
                      <Play className="w-4 h-4 mr-2" />
                      Play
                    </Button>
                  </Link>
                  <Link to="/pearl-pop">
                    <Button variant="outline" className="border-bright-coral text-bright-coral hover:bg-bright-coral hover:text-white">
                      <Info className="w-4 h-4" />
                    </Button>
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/pearl-pop" className="flex-1">
                    <Button variant="outline" className="w-full border-medium-purple text-medium-purple hover:bg-medium-purple hover:text-white">
                      <Info className="w-4 h-4 mr-2" />
                      Learn More
                    </Button>
                  </Link>
                  <Link to="/subscription">
                    <Button className="bg-medium-purple hover:bg-deep-purple text-white">
                      <Lock className="w-4 h-4" />
                    </Button>
                  </Link>
                </>
              )}
            </CardFooter>
          </Card>

          {/* Character Creator Card */}
          <Card className={`bg-white/90 backdrop-blur-sm border-2 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1 group relative overflow-hidden ${isPremium ? 'border-pale-aqua hover:border-medium-teal' : 'border-medium-purple/50'}`}>
            {!isPremium && (
              <div className="absolute top-2 right-2 bg-medium-purple text-white text-xs px-2 py-1 rounded-full font-bold flex items-center gap-1">
                <Lock className="w-3 h-3" />
                Premium
              </div>
            )}
            <CardHeader className="pb-2">
              <div className="text-4xl mb-2">🎨</div>
              <CardTitle className={`text-xl text-dark-navy transition-colors ${isPremium ? 'group-hover:text-medium-teal' : 'group-hover:text-medium-purple'}`}>Character Creator</CardTitle>
              <CardDescription className="text-medium-purple font-medium text-sm">Design Your Hero</CardDescription>
            </CardHeader>
            <CardContent className="pb-4">
              <p className="text-dark-navy/70 text-sm">
                Customize your character's look for Ocean Dash and Pollution Patrol!
              </p>
            </CardContent>
            <CardFooter className="flex gap-2">
              {isPremium ? (
                <>
                  <Link to="/character-creation" className="flex-1">
                    <Button className="w-full bg-medium-teal hover:bg-light-teal text-white">
                      <Play className="w-4 h-4 mr-2" />
                      Create
                    </Button>
                  </Link>
                  <Link to="/character-creator">
                    <Button variant="outline" className="border-medium-teal text-medium-teal hover:bg-medium-teal hover:text-white">
                      <Info className="w-4 h-4" />
                    </Button>
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/character-creator" className="flex-1">
                    <Button variant="outline" className="w-full border-medium-purple text-medium-purple hover:bg-medium-purple hover:text-white">
                      <Info className="w-4 h-4 mr-2" />
                      Learn More
                    </Button>
                  </Link>
                  <Link to="/subscription">
                    <Button className="bg-medium-purple hover:bg-deep-purple text-white">
                      <Lock className="w-4 h-4" />
                    </Button>
                  </Link>
                </>
              )}
            </CardFooter>
          </Card>
        </div>
      </div>

      {/* Conservation Message */}
      <div className="max-w-4xl mx-auto text-center p-8 bg-white/60 backdrop-blur-md rounded-2xl border border-white/40 shadow-sm">
        <h2 className="text-2xl font-bold text-dark-navy mb-2">Protect Our Oceans</h2>
        <p className="text-dark-navy/80 mb-6">
          When you subscribe, we contribute to removing CO₂ from the atmosphere. <br/> This helps the protect the oceanss and its creatures.
        </p>
        <Link to="/helping-the-environment">
          <Button variant="outline" className="border-medium-teal text-medium-teal hover:bg-medium-teal hover:text-white transition-all">
            Learn How We Help
          </Button>
        </Link>
      </div>
    </div>
  )
}
